import type { ReactNode } from 'react';
import { WORD_LENGTH } from '@lexicon/core';
import { CalendarIcon } from './icons';
import { useLanguage, useStrings } from '../state/AppContext';

export interface DailyBannerProps {
  date: Date;
  /** Today's ladder length (spec §10.2). The badge appears on non-5 days. */
  length?: number;
  /** True while this length's accepted-guess chunk is still loading. */
  wordListPending?: boolean;
  /** Right-hand slot: "View result" once the game is finished. */
  action?: ReactNode;
  /** Second row inside the same glass card (the stat strip). */
  children?: ReactNode;
}

export function DailyBanner({ date, length, wordListPending = false, action, children }: DailyBannerProps) {
  const t = useStrings();
  const language = useLanguage();
  // The date reads in the language being played, not the device's: an
  // otherwise-Dutch banner saying "August 20" is the one English word left
  // on the screen. `undefined` (device locale) was the pre-Dutch default
  // and is what `language === 'en'` still resolves to for an English
  // player on an English device.
  const label = new Intl.DateTimeFormat(language, { month: 'long', day: 'numeric' }).format(date);
  const badge = length !== undefined && length !== WORD_LENGTH ? length : null;
  return (
    <section className="glass overflow-hidden rounded-2xl" aria-label={t.daily.sectionLabel}>
      <div className="flex items-center gap-2 px-4 py-1 sm:py-3">
        <CalendarIcon className="h-4 w-4 shrink-0 text-dim" />
        <span className="shrink-0 whitespace-nowrap text-xs font-semibold tracking-[0.18em]">
          {t.daily.heading}
        </span>
        {badge !== null ? (
          <span
            className={[
              'shrink-0 whitespace-nowrap rounded-full border border-hairline px-2 py-0.5',
              'text-[10px] font-semibold tracking-[0.12em] text-accent',
              wordListPending ? 'animate-pulse' : '',
            ].join(' ').trim()}
            {...(wordListPending ? { title: t.toast.validationPending } : {})}
          >
            {t.daily.letters(badge)}
          </span>
        ) : null}
        {/* Below 390px there isn't room for a legible date next to "DAILY
            CHALLENGE" and the action; with a length badge in the row too,
            that threshold moves out to 460px. Hiding the divider and date
            as a unit reads better than a dangling "|" and a stray letter.
            Both class strings are written out in full so Tailwind's
            scanner sees them. */}
        <div className={badge !== null
          ? 'hidden min-w-0 flex-1 items-center gap-2 min-[460px]:flex'
          : 'hidden min-w-0 flex-1 items-center gap-2 min-[390px]:flex'}
        >
          <span className="shrink-0 text-dim" aria-hidden="true">|</span>
          <span className="min-w-0 flex-1 truncate text-sm text-dim">{label}</span>
        </div>
        {action ? <div className="ml-auto shrink-0 whitespace-nowrap">{action}</div> : null}
      </div>
      {children ? <div className="border-t border-hairline">{children}</div> : null}
    </section>
  );
}
